export interface SearchFilters {
  text: string;
  authorIds: string[];
  roomIds: string[];
  before?: number;
  after?: number;
  hasFile: boolean;
}

const OPERATOR = /(?:^|\s)(from|in|before|after|has):(?:"([^"]+)"|(\S+))/gi;

export function parseDateValue(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d{13}$/.test(trimmed)) return Number(trimmed);
  if (/^\d{10}$/.test(trimmed)) return Number(trimmed) * 1000;
  const match = /^(\d{4})[-/](\d{2})[-/](\d{2})$/.exec(trimmed);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const ms = Date.UTC(year, month - 1, day);
  const dt = new Date(ms);
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return undefined;
  return ms;
}

export function parseSearchQuery(input: string): SearchFilters {
  const authorIds: string[] = [];
  const roomIds: string[] = [];
  let before: number | undefined;
  let after: number | undefined;
  let hasFile = false;
  const remainder = (input ?? "").replace(OPERATOR, (_all, key: string, quoted?: string, bare?: string) => {
    const value = (quoted ?? bare ?? "").trim();
    const op = String(key).toLowerCase();
    if (op === "from" && value) authorIds.push(value.replace(/^@/, ""));
    else if (op === "in" && value) roomIds.push(value.replace(/^#/, ""));
    else if (op === "before") {
      const ms = parseDateValue(value);
      if (ms != null) before = ms;
    } else if (op === "after") {
      const ms = parseDateValue(value);
      if (ms != null) after = ms;
    } else if (op === "has" && /^(file|files|attachment|attachments)$/i.test(value)) {
      hasFile = true;
    }
    return " ";
  });
  return {
    text: remainder.replace(/\s+/g, " ").trim(),
    authorIds: unique(authorIds),
    roomIds: unique(roomIds),
    before,
    after,
    hasFile,
  };
}

export function toDateInputValue(ms?: number): string {
  if (ms == null || !Number.isFinite(ms)) return "";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export function fromDateInputValue(value: string, endOfDay = false): number | undefined {
  if (!value) return undefined;
  const ms = parseDateValue(value);
  if (ms == null) return undefined;
  return endOfDay ? ms + 86_400_000 : ms;
}

export function filtersToOperatorQuery(filters: SearchFilters): string {
  const parts = [filters.text];
  for (const id of filters.authorIds) parts.push(`from:${id}`);
  for (const id of filters.roomIds) parts.push(`in:${id}`);
  if (filters.after) parts.push(`after:${new Date(filters.after).toISOString().slice(0, 10)}`);
  if (filters.before) parts.push(`before:${new Date(filters.before).toISOString().slice(0, 10)}`);
  if (filters.hasFile) parts.push("has:file");
  return parts.filter(Boolean).join(" ");
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}
